<template>
  <modal
    elem-id="modal_clone_to_franchise"
    :show="isVisible"
    :title="t('Clonar para Franquia')"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isSaving ? t('Clonando...') : t('Clonar')"
    :save-fn="handleClone"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <p class="clone-info">
        {{ t('Clonando') }} <strong>{{ selectedCount }}</strong> {{ t('registros para outra franquia') }}
      </p>

      <div class="form-field">
        <SelectList
          elem-id="cloneTargetFranchise"
          data-testid="clone-target-franchise-select"
          v-model="targetFranchiseId"
          :itens-select="availableFranchises"
          :label="t('Franquia destino')"
          :placeholder="t('Selecione a franquia destino')"
          required
          :validation="() => targetFranchiseId != null"
          :auto-select-first="false"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SelectList from '../../inputs/SelectList.vue';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface FranchiseOption {
  ID: Id;
  Name: string;
}

interface Props {
  franchises?: FranchiseOption[];
  excludeFranchiseIds?: Array<Id>;
}

const props = withDefaults(defineProps<Props>(), {
  franchises: () => [],
  excludeFranchiseIds: () => []
});

const emit = defineEmits<{
  'clone': [payload: { ids: Id[]; targetFranchiseId: Id }];
  'close': [];
}>();

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const targetFranchiseId = ref<Id | undefined>(undefined);
const selectedIds = ref<Id[]>([]);
const selectedCount = ref<number>(0);

const availableFranchises = computed<FranchiseOption[]>(() =>
  props.franchises.filter((f: FranchiseOption) =>
    !props.excludeFranchiseIds.includes(Number(f.ID)) &&
    !props.excludeFranchiseIds.includes(String(f.ID))
  )
);

const open = (ids: Id[]): void => {
  log('CloneToFranchiseModal', 'open', 'Opening with', ids.length, 'records');
  selectedIds.value = ids;
  selectedCount.value = ids.length;
  targetFranchiseId.value = undefined;
  isVisible.value = true;
};

const handleClose = (): void => {
  isVisible.value = false;
  isSaving.value = false;
  targetFranchiseId.value = undefined;
  selectedIds.value = [];
  emit('close');
};

const handleClone = async (): Promise<void> => {
  if (!targetFranchiseId.value) return;

  isSaving.value = true;
  try {
    emit('clone', {
      ids: selectedIds.value,
      targetFranchiseId: targetFranchiseId.value
    });
    handleClose();
  } catch (err) {
    logError('CloneToFranchiseModal', 'handleClone', 'Error', err);
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<style scoped>
.clone-info {
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: var(--p-text-color);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
</style>
